import React, { useEffect, useRef, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FileText, Upload, Trash2, Download, FileSpreadsheet, FileCheck, FileBarChart2, Boxes, Files } from "lucide-react";
import { toast } from "sonner";

export const DOC_TYPES = [
  { value: "balance", label: "Balance / financiero", icon: FileBarChart2 },
  { value: "contract", label: "Contrato / sociedad", icon: FileCheck },
  { value: "lease", label: "Contrato de arriendo", icon: FileText },
  { value: "permits", label: "Permisos y patentes", icon: FileCheck },
  { value: "inventory", label: "Inventario detallado", icon: Boxes },
  { value: "other", label: "Otro", icon: Files },
];

export function docTypeLabel(t) {
  return DOC_TYPES.find((d) => d.value === t)?.label || "Otro";
}
export function DocTypeIcon({ type, className = "w-4 h-4" }) {
  const Icon = (DOC_TYPES.find((d) => d.value === type) || DOC_TYPES[5]).icon;
  return <Icon className={className} />;
}

function bytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function BusinessDocuments({ businessId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", doc_type: "balance" });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/seller/businesses/${businessId}/documents`);
      setDocs(r.data);
    } catch {
      toast.error("No se pudieron cargar los documentos");
    }
  };
  useEffect(() => { load(); }, [businessId]);

  const upload = async () => {
    if (!file) { toast.error("Selecciona un archivo"); return; }
    if (!form.name.trim()) { toast.error("Ingresa un nombre para el documento"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ name: form.name.trim(), doc_type: form.doc_type });
      await api.post(`/seller/businesses/${businessId}/documents?${params}`, fd);
      toast.success("Documento subido");
      setForm({ name: "", doc_type: "balance" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este documento? Los inversionistas con acceso aprobado dejarán de verlo.")) return;
    try {
      await api.delete(`/seller/documents/${id}`);
      toast.success("Documento eliminado");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const download = (doc) => {
    const token = localStorage.getItem("lln_token");
    // Open in new tab with auth — use a temporary fetch approach
    fetch(`${API_BASE}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("Acceso denegado"); return r.blob(); })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = doc.name;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("No se pudo descargar"));
  };

  return (
    <div className="space-y-4" data-testid="seller-documents">
      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-5 h-5 text-amber-600" />
          <h3 className="font-display text-base font-bold">Documentos privados</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Sube documentos sensibles (balance, contrato, patentes). Los inversionistas verán el listado pero deberán <strong>pedirte acceso</strong> y tú decides quién puede descargarlos.
        </p>

        <div className="grid sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-5">
            <Label className="text-xs">Nombre del documento</Label>
            <Input
              value={form.name}
              placeholder="Ej. Balance anual 2025"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="doc-name-input"
            />
          </div>
          <div className="sm:col-span-3">
            <Label className="text-xs">Tipo</Label>
            <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
              <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-4">
            <Label className="text-xs">Archivo (máx 15MB)</Label>
            <Input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
          </div>
          <div className="sm:col-span-12 flex justify-end">
            <Button onClick={upload} disabled={loading} className="btn-investment" data-testid="doc-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> {loading ? "Subiendo…" : "Subir documento"}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {docs.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">Aún no has subido documentos.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-4 py-3" data-testid={`doc-row-${d.id}`}>
                <div className="w-9 h-9 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                  <DocTypeIcon type={d.doc_type} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.name}</p>
                  <p className="text-xs text-slate-500">{docTypeLabel(d.doc_type)} · {bytes(d.size_bytes)}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => download(d)} data-testid={`doc-download-${d.id}`}>
                  <Download className="w-4 h-4 mr-1" /> Ver
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="text-rose-600 hover:bg-rose-50" data-testid={`doc-remove-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
